// ======================================================
// REHBER360 — AKILLI PLAN ROBOTU
// ANA PLAN MOTORU
// ======================================================
//
// Bu dosya robotun ORKESTRASYON katmanıdır.
//
// Görevi:
//
// 1. Slot Builder'ı çalıştırmak
// 2. Slotları Scheduler'a vermek
// 3. Oluşan takvimi Validator'dan geçirmek
// 4. Tüm hata / uyarı / özetleri tek sonuçta toplamak
//
// Bu dosya:
// - Müfredat bilmez
// - Ders seçmez
// - Konu üretmez
// - Takvim algoritması içermez
//
// Alt katmanları sırayla çalıştırır.
//
// ÖNEMLİ:
// Date.now(), Math.random() vb. kullanılmaz.
// Aynı girdi → aynı mantıksal çıktı.
// ======================================================

import {
  buildSmartPlanSlots
} from './smartPlanSlotBuilder';

import {
  scheduleSmartPlanSlots
} from './smartPlanScheduler';

import {
  validateSmartPlanSchedule
} from './smartPlanValidator';


// ======================================================
// 1. ENGINE SÜRÜMÜ
// ======================================================

export const SMART_PLAN_ENGINE_VERSION =
  '1.0.0';


// ======================================================
// 2. ENGINE AŞAMALARI
// ======================================================

export const SMART_PLAN_ENGINE_STAGES = {
  SLOT_BUILDER: 'slot_builder',
  SCHEDULER: 'scheduler',
  VALIDATOR: 'validator',
  COMPLETE: 'complete'
};


// ======================================================
// 3. ENGINE SONUÇ DURUMLARI
// ======================================================

export const SMART_PLAN_ENGINE_STATUS = {
  READY: 'ready',
  PARTIAL: 'partial',
  FAILED: 'failed'
};


// ======================================================
// 4. ISSUE LİSTELERİNİ BİRLEŞTİR
// ======================================================
//
// Farklı katmanlardan aynı hata/uyarı birebir
// gelirse yalnızca bir kez döndürülür.
//
// Farklı detay taşıyan aynı kodlar korunur.
//
// Örn:
//
// {
//   code: "SUBJECT_CLUSTERED",
//   subject: "Matematik"
// }
//
// ve
//
// {
//   code: "SUBJECT_CLUSTERED",
//   subject: "Fizik"
// }
//
// birbirinden farklı kayıtlardır.
// ======================================================

const mergeIssues = (
  ...issueGroups
) => {

  const result = [];

  const seen =
    new Set();


  issueGroups
    .flat()
    .filter(Boolean)
    .forEach(issue => {

      let key;

      try {

        key =
          JSON.stringify(
            issue
          );
      }

      catch {

        key =
          String(
            issue?.code ||
            result.length
          );
      }


      if (
        seen.has(key)
      ) {
        return;
      }


      seen.add(key);

      result.push(issue);
    });


  return result;
};


// ======================================================
// 5. BAŞARISIZ SLOT BUILDER SONUCU
// ======================================================

const createSlotBuilderFailure = ({
  config,
  slotResult,
  extraErrors = []
}) => {

  return {

    success: false,

    status:
      SMART_PLAN_ENGINE_STATUS.FAILED,

    stage:
      SMART_PLAN_ENGINE_STAGES
        .SLOT_BUILDER,

    engineVersion:
      SMART_PLAN_ENGINE_VERSION,

    configSummary: {
      weeks:
        Number(
          config?.weeks || 0
        ),

      dailyMinutes:
        Number(
          config?.dailyMinutes || 0
        ),

      workingDayCount:
        Array.isArray(
          config?.workingDays
        )
          ? config.workingDays.length
          : 0,

      strategyId:
        config?.strategyId || null,

      examMode:
        config?.examMode || null,

      field:
        config?.field || null
    },

    slots: [],

    schedule: [],

    scheduledSlots: [],

    unscheduledSlots: [],

    errors:
      mergeIssues(
        slotResult?.errors || [],
        extraErrors
      ),

    warnings:
      mergeIssues(
        slotResult?.warnings || []
      ),

    validation: null,

    summary: {
      slotBuilder:
        slotResult?.summary || null,

      scheduler: null,

      validator: null
    }
  };
};


// ======================================================
// 6. PLAN SONUÇ DURUMUNU BELİRLE
// ======================================================

const resolveEngineStatus = ({
  success,
  scheduledCount,
  unscheduledCount
}) => {

  if (success) {
    return (
      SMART_PLAN_ENGINE_STATUS.READY
    );
  }


  if (
    scheduledCount > 0 ||
    unscheduledCount > 0
  ) {

    return (
      SMART_PLAN_ENGINE_STATUS.PARTIAL
    );
  }


  return (
    SMART_PLAN_ENGINE_STATUS.FAILED
  );
};


// ======================================================
// 7. CONFIG ÖZETİ
// ======================================================
//
// UI ve raporlama için küçük,
// güvenli bir özet.
//
// Config'in tamamını sonuç nesnesine kopyalamıyoruz.
// ======================================================

const buildConfigSummary = config => {

  return {

    weeks:
      Number(
        config?.weeks || 0
      ),

    dailyMinutes:
      Number(
        config?.dailyMinutes || 0
      ),

    workingDays:
      Array.isArray(
        config?.workingDays
      )
        ? [...config.workingDays]
        : [],

    workingDayCount:
      Array.isArray(
        config?.workingDays
      )
        ? config.workingDays.length
        : 0,

    examMode:
      config?.examMode || null,

    field:
      config?.field || null,

    strategyId:
      config?.strategyId || null,

    difficulty:
      config?.difficulty || null,

    studentLevel:
      config?.studentLevel || null,

    selectedSubjects:
      Array.isArray(
        config?.selectedSubjects
      )
        ? [...config.selectedSubjects]
        : []
  };
};


// ======================================================
// 8. ENGINE PIPELINE ÖZETİ
// ======================================================

const buildPipelineSummary = ({
  slotResult,
  scheduleResult,
  validationResult
}) => {

  return {

    slotBuilder: {

      success:
        slotResult?.success === true,

      slotCount:
        Array.isArray(
          slotResult?.slots
        )
          ? slotResult.slots.length
          : 0,

      generatedMinutes:
        Number(
          slotResult
            ?.summary
            ?.generatedMinutes || 0
        ),

      unusedMinutes:
        Number(
          slotResult
            ?.summary
            ?.unusedMinutes || 0
        )
    },


    scheduler: {

      success:
        scheduleResult?.success ===
        true,

      scheduledCount:
        Number(
          scheduleResult
            ?.summary
            ?.scheduledCount || 0
        ),

      unscheduledCount:
        Number(
          scheduleResult
            ?.summary
            ?.unscheduledCount || 0
        ),

      scheduledMinutes:
        Number(
          scheduleResult
            ?.summary
            ?.scheduledMinutes || 0
        ),

      unscheduledMinutes:
        Number(
          scheduleResult
            ?.summary
            ?.unscheduledMinutes || 0
        ),

      usedDayCount:
        Number(
          scheduleResult
            ?.summary
            ?.usedDayCount || 0
        )
    },


    validator: {

      valid:
        validationResult?.valid ===
        true,

      errorCount:
        Array.isArray(
          validationResult?.errors
        )
          ? validationResult
              .errors.length
          : 0,

      warningCount:
        Array.isArray(
          validationResult?.warnings
        )
          ? validationResult
              .warnings.length
          : 0,

      metrics:
        validationResult?.metrics ||
        null
    }
  };
};


// ======================================================
// 9. ANA AKILLI PLAN MOTORU
// ======================================================
//
// Kullanım:
//
// const result = generateSmartPlan({
//   weeks: 4,
//   examMode: "tyt",
//   strategyId: "dengeli",
//   dailyMinutes: 120,
//   workingDays: [
//     "Pazartesi",
//     "Salı",
//     "Çarşamba",
//     "Perşembe",
//     "Cuma"
//   ],
//   selectedSubjects: [
//     "TYT - Matematik",
//     "TYT - Türkçe"
//   ]
// });
//
// ======================================================

const generateSmartPlanCore = config => {

  // ====================================================
  // A. SLOT BUILDER
  // ====================================================

  const slotResult =
    buildSmartPlanSlots(
      config
    );


  // Slot Builder input validation'dan geçemediyse
  // pipeline burada durur.
  if (
    !slotResult?.success
  ) {

    return (
      createSlotBuilderFailure({
        config,
        slotResult
      })
    );
  }


  // ----------------------------------------------------
  // SLOT BUILDER BAŞARILI AMA HİÇ SLOT YOK
  // ----------------------------------------------------
  //
  // Teknik olarak başarılı olsa bile kullanıcıya
  // boş planı başarılı plan olarak göstermiyoruz.
  // ----------------------------------------------------

  const sourceSlots =
    Array.isArray(
      slotResult.slots
    )
      ? slotResult.slots
      : [];


  if (
    sourceSlots.length === 0
  ) {

    return (
      createSlotBuilderFailure({
        config,

        slotResult,

        extraErrors: [
          {
            code:
              'NO_PLAN_SLOTS_GENERATED'
          }
        ]
      })
    );
  }


  // ====================================================
  // B. SCHEDULER
  // ====================================================

  const scheduleResult =
    scheduleSmartPlanSlots({
      slots:
        sourceSlots,

      config
    });


  // Scheduler success=false olduğunda burada hemen
  // durmuyoruz.
  //
  // Çünkü bazı slotlar yerleşmiş,
  // bazıları yerleşmemiş olabilir.
  //
  // Validator yine çalışmalı ve neden başarısız
  // olduğunu ayrıntılı biçimde raporlamalı.
  // ====================================================


    // ====================================================
  // C. FINAL VALIDATOR
  // ====================================================

  const supplementalSlots =
    Array.isArray(
      scheduleResult
        ?.supplementalSlots
    )
      ? scheduleResult
          .supplementalSlots
      : [];


  const validationSourceSlots = [
    ...sourceSlots,
    ...supplementalSlots
  ];


  const validationResult =
    validateSmartPlanSchedule({
      scheduleResult,

      config,

      sourceSlots:
        validationSourceSlots
    });


  // ====================================================
  // D. HATALAR
  // ====================================================

  const errors =
    mergeIssues(

      slotResult?.errors || [],

      validationResult
        ?.errors || []
    );


  // ====================================================
  // E. UYARILAR
  // ====================================================

  const warnings =
    mergeIssues(

      slotResult
        ?.warnings || [],

      scheduleResult
        ?.warnings || [],

      validationResult
        ?.warnings || []
    );


  // ====================================================
  // F. FINAL BAŞARI
  // ====================================================

  const success =
    slotResult?.success === true &&
    scheduleResult?.success === true &&
    validationResult?.valid === true &&
    errors.length === 0;


  // ====================================================
  // G. SAYILAR
  // ====================================================

  const scheduledSlots =
    Array.isArray(
      scheduleResult
        ?.scheduledSlots
    )
      ? scheduleResult
          .scheduledSlots
      : [];


  const unscheduledSlots =
    Array.isArray(
      scheduleResult
        ?.unscheduledSlots
    )
      ? scheduleResult
          .unscheduledSlots
      : [];


  const status =
    resolveEngineStatus({
      success,

      scheduledCount:
        scheduledSlots.length,

      unscheduledCount:
        unscheduledSlots.length
    });




  // ====================================================
  // H. PIPELINE ÖZETİ
  // ====================================================

  const pipeline =
    buildPipelineSummary({
      slotResult,
      scheduleResult,
      validationResult
    });


  // ====================================================
  // I. FINAL SONUÇ
  // ====================================================

  return {

    success,

    status,

    stage:
      success
        ? SMART_PLAN_ENGINE_STAGES
            .COMPLETE
        : (
            validationResult
              ?.valid === false
              ? SMART_PLAN_ENGINE_STAGES
                  .VALIDATOR
              : SMART_PLAN_ENGINE_STAGES
                  .SCHEDULER
          ),

    engineVersion:
      SMART_PLAN_ENGINE_VERSION,


    // --------------------------------------------------
    // CONFIG
    // --------------------------------------------------

    configSummary:
      buildConfigSummary(
        config
      ),


    // --------------------------------------------------
    // HAM SLOT HAVUZU
    // --------------------------------------------------

        slots:
      validationSourceSlots,

    supplementalSlots,


    // --------------------------------------------------
    // TAKVİM
    // --------------------------------------------------

    schedule:
      Array.isArray(
        scheduleResult?.schedule
      )
        ? scheduleResult.schedule
        : [],


    // --------------------------------------------------
    // YERLEŞTİRİLENLER
    // --------------------------------------------------

    scheduledSlots,


    // --------------------------------------------------
    // YERLEŞTİRİLEMEYENLER
    // --------------------------------------------------

    unscheduledSlots,


    // --------------------------------------------------
    // HATA / UYARI
    // --------------------------------------------------

    errors,

    warnings,


    // --------------------------------------------------
    // VALIDATOR SONUCU
    // --------------------------------------------------

    validation:
      validationResult,


    // --------------------------------------------------
    // TOPLU ÖZET
    // --------------------------------------------------

    summary: {

      pipeline,

      slotBuilder:
        slotResult?.summary ||
        null,

      scheduler:
        scheduleResult?.summary ||
        null,

      validator:
        validationResult?.metrics ||
        null
    }
  };
};



// ======================================================
// 10. TOPIC COVERAGE RECOMMENDATION
// ======================================================
//
// SlotBuilder:
//
// "Kapsam eksik."
//
// der.
//
// Engine:
//
// "Bu kapsamı tamamlamak için ne yapabilirsin?"
//
// sorusunu cevaplar.
//
// ÖNEMLİ:
//
// generateSmartPlanCore kendi kendisini çağırmaz.
//
// Minimum hafta araştırmasında önce yalnız
// SlotBuilder kullanılır.
//
// SlotBuilder'ın coverage warning'inin ilk kez
// kaybolduğu aday hafta daha sonra gerçek CORE
// pipeline ile doğrulanır.
//
// Böylece:
//
// - recursion oluşmaz,
// - gereksiz Scheduler probe'ları azaltılır,
// - gerçek final plan READY olmadan öneri verilmez.
// ======================================================

const getTopicCoverageWarning = result => {

  return (
    (
      result?.warnings || []
    )
      .find(
        warning =>
          warning?.code ===
            'TOPIC_COVERAGE_INSUFFICIENT'
      ) ||
    null
  );
};


// ------------------------------------------------------
// MİNİMUM UYGULANABİLİR HAFTAYI BUL
// ------------------------------------------------------

const findMinimumTopicCoverageWeeks = ({
  config,
  currentWeeks
}) => {

  const maxProbeWeeks =
    36;


  for (
    let probeWeeks =
      currentWeeks + 1;

    probeWeeks <=
      maxProbeWeeks;

    probeWeeks += 1
  ) {

    const probeConfig = {

      ...config,

      weeks:
        probeWeeks
    };


    // --------------------------------------------------
    // 1. UCUZ ÖN KONTROL
    // --------------------------------------------------
    //
    // Coverage warning SlotBuilder tarafından
    // üretildiği için önce yalnız SlotBuilder çalışır.
    // --------------------------------------------------

    const probeSlotResult =
      buildSmartPlanSlots(
        probeConfig
      );


    if (
      probeSlotResult
        ?.success !== true
    ) {

      continue;
    }


    const probeSlotCoverageWarning =
      getTopicCoverageWarning(
        probeSlotResult
      );


    // Hâlâ eksik kapsam varsa pahalı
    // Scheduler / Validator pipeline'ına geçme.
    if (
      probeSlotCoverageWarning
    ) {

      continue;
    }


    // --------------------------------------------------
    // 2. GERÇEK MOTOR DOĞRULAMASI
    // --------------------------------------------------
    //
    // SlotBuilder açısından kapsam tamam.
    //
    // Şimdi Scheduler + Validator dahil gerçek motor
    // sonucunun da READY olduğunu doğrula.
    // --------------------------------------------------

    const probeResult =
      generateSmartPlanCore(
        probeConfig
      );


    const probeFinalCoverageWarning =
      getTopicCoverageWarning(
        probeResult
      );


    const probeFullyFeasible =
      probeResult
        ?.success === true &&

      probeResult
        ?.status ===
          SMART_PLAN_ENGINE_STATUS
            .READY &&

      !probeFinalCoverageWarning;


    if (
      probeFullyFeasible
    ) {

      return probeWeeks;
    }
  }


  return null;
};


// ------------------------------------------------------
// COVERAGE RECOMMENDATION OLUŞTUR
// ------------------------------------------------------

const buildTopicCoverageRecommendation = ({
  config,
  result
}) => {

  const coverageWarning =
    getTopicCoverageWarning(
      result
    );


  // Tam kapsam varsa hiçbir öneri üretme.
  if (
    !coverageWarning
  ) {

    return null;
  }


  const currentWeeks =
    Number(
      config?.weeks || 0
    );


  if (
    !Number.isInteger(
      currentWeeks
    ) ||
    currentWeeks < 1
  ) {

    return null;
  }


  const minimumFeasibleWeeks =
    findMinimumTopicCoverageWeeks({
      config,
      currentWeeks
    });


  // 36 haftaya kadar çözüm bulunamadıysa
  // şimdilik sahte öneri üretme.
  if (
    !Number.isInteger(
      minimumFeasibleWeeks
    )
  ) {

    return null;
  }


  const minimumAdditionalWeeks =
    Math.max(
      0,

      minimumFeasibleWeeks -
      currentWeeks
    );


  return {

    code:
      'EXTEND_PLAN_DURATION',

    currentWeeks,

    minimumFeasibleWeeks,

    minimumAdditionalWeeks,

    targetTopicCount:
      Number(
        coverageWarning
          ?.targetTopicCount || 0
      ),

    coveredTopicCount:
      Number(
        coverageWarning
          ?.coveredTopicCount || 0
      ),

    missingTopicCount:
      Number(
        coverageWarning
          ?.missingTopicCount || 0
      ),

    coveragePercent:
      Number(
        coverageWarning
          ?.coveragePercent || 0
      )
  };
};


// ======================================================
// 11. PUBLIC ENGINE
// ======================================================
//
// Ürünün dışarıdan çağırdığı fonksiyon budur.
//
// Core sonucu değiştirilmez.
// Yalnız gerçekten gerekli olduğunda
// recommendation alanı eklenir.
// ======================================================

export const generateSmartPlan = config => {

  const result =
    generateSmartPlanCore(
      config
    );


  const topicCoverageRecommendation =
    buildTopicCoverageRecommendation({
      config,
      result
    });


  // Recommendation gerekmiyorsa eski result
  // birebir korunur.
  if (
    !topicCoverageRecommendation
  ) {

    return result;
  }


  return {

    ...result,

    recommendations: {

      ...(
        result?.recommendations ||
        {}
      ),

      topicCoverage:
        topicCoverageRecommendation
    }
  };
};


// ======================================================
// 10. PLAN ÜRETİLEBİLDİ Mİ?
// ======================================================
//
// UI'da gerektiğinde:
//
// if (isSmartPlanReady(result)) { ... }
//
// şeklinde kullanılabilir.
// ======================================================

export const isSmartPlanReady = result => {

  return (
    result?.success === true &&
    result?.status ===
      SMART_PLAN_ENGINE_STATUS.READY &&
    Array.isArray(
      result?.schedule
    ) &&
    result.schedule.length > 0
  );
};


// ======================================================
// 11. KISMİ PLAN MI?
// ======================================================

export const isSmartPlanPartial = result => {

  return (
    result?.status ===
    SMART_PLAN_ENGINE_STATUS.PARTIAL
  );
};


// ======================================================
// 12. MOTOR HATA KODLARI
// ======================================================
//
// Şimdilik yalnız engine'e özgü hata.
//
// Diğer hata kodları Rules, Scheduler ve
// Validator katmanlarından gelir.
//
// İleride UI'da kullanıcı dostu mesajlar için ayrı
// errorMessages.js oluşturabiliriz.
// ======================================================

export const SMART_PLAN_ENGINE_ERROR_CODES = {
  NO_PLAN_SLOTS_GENERATED:
    'NO_PLAN_SLOTS_GENERATED'
};



// ======================================================
// GEÇİCİ LOCALHOST SMOKE TEST KAPISI
// ======================================================
//
// Yalnızca geliştirme sırasında kullanılacaktır.
//
// localhost / 127.0.0.1 üzerinde:
//
// ?smartPlanTest=1
//
// eklendiğinde SmokeTest modülü dinamik olarak yüklenir.
//
// Production domainlerinde çalışmaz.
//
// Go-live öncesinde bu bloğu tekrar kaldıracağız.
// ======================================================

if (
  typeof window !== 'undefined'
) {

  const isLocalSmartPlanTestHost =
    [
      'localhost',
      '127.0.0.1'
    ].includes(
      window.location.hostname
    );


  const shouldRunSmartPlanSmokeTest =
    isLocalSmartPlanTestHost &&
    new URLSearchParams(
      window.location.search
    ).get(
      'smartPlanTest'
    ) === '1';


  if (
    shouldRunSmartPlanSmokeTest &&
    !window.__rehber360SmartPlanSmokeTestRan
  ) {

    window.__rehber360SmartPlanSmokeTestRan =
      true;


    import(
      './smartPlanSmokeTest'
    )
      .then(
        ({
          runSmartPlanSmokeTests
        }) => {

          runSmartPlanSmokeTests(
            generateSmartPlan
          );
        }
      )
      .catch(
        error => {

          console.error(
            'Akıllı Plan smoke test yüklenemedi:',
            error
          );
        }
      );
  }
}

